import { InfoIcon } from "lucide-react";
import { type ReactNode, useCallback } from "react";

import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { Button } from "@/web/components/primitives/button";
import { Checkbox } from "@/web/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/primitives/dialog";
import { Input } from "@/web/components/primitives/input";
import { Label } from "@/web/components/primitives/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { DollarSelector } from "@/web/components/styled/DollarSelector";
import { Field, FieldGroup, FieldLabel } from "@/web/components/styled/Field";
import {
  PLEDGE_CONSENT_LABEL,
  PLEDGE_MESSAGE_LABEL,
  PLEDGE_MESSAGE_PLACEHOLDER,
} from "@/web/components/styled/pledgeFormState";
import { formatUSDShort } from "@/web/lib/formatting";
import { cn } from "@/web/lib/styling";

// ---------------------------------------------------------------------------
// Shared pledge / expression-of-interest dialog.
//
// One component for every "pledge" surface (TTW shareable card, browse-page
// nomination, Community Theater). Owns the form fields, validation, and the
// post-submit "create an account" prompt so the four flows can't drift apart.
// Per-context differences (which backend to call, success handling) live in the
// parent — seeding, defaults, and submit fields live in pledgeFormState.ts.
//
// Field set is identical across contexts: amount, first name*, last name*,
// email* (anonymous only), supportive message, and a privacy checkbox. `*` =
// required. Privacy is opt-in: pledgers are public by default and check the box
// to keep their name/message hidden (the box is the inverse of the stored
// `consentToDisplay` flag — see PledgeFormBody).
// ---------------------------------------------------------------------------

export const SUPPORTIVE_MESSAGE_MAX_LENGTH = 280;

export type PledgeFormState = {
  amount: number | undefined;
  firstName: string;
  lastName: string;
  /** Only collected/required for anonymous pledges; ignored when signed in. */
  email: string;
  supportiveMessage: string;
  /**
   * Backend semantics: `true` = show the pledger's name/message publicly.
   * The form surfaces this as an *inverted* "don't display me" checkbox, so a
   * fresh pledge defaults to `true` (public, checkbox unchecked).
   */
  consentToDisplay: boolean;
};

export const EMPTY_PLEDGE_FORM_STATE: PledgeFormState = {
  amount: undefined,
  firstName: "",
  lastName: "",
  email: "",
  supportiveMessage: "",
  consentToDisplay: true,
};

/** Loose client-side email shape check — the server re-validates. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Whether the form is submittable. Centralized so every caller (and the
 * dialog's own submit button) shares one definition of "valid". `hasChanges`
 * is the parent's edit-detection result (true when there's nothing to diff
 * against, e.g. a fresh pledge).
 */
export function canSubmitPledge(formState: PledgeFormState, isSignedIn: boolean): boolean {
  if (formState.amount === undefined || formState.amount <= 0) return false;
  if (!formState.firstName.trim()) return false;
  if (formState.supportiveMessage.length > SUPPORTIVE_MESSAGE_MAX_LENGTH) return false;
  // Last name is required for everyone; email + last name doubly enforced for
  // anonymous pledges (the email keys the EOI server-side).
  if (!formState.lastName.trim()) return false;
  if (!isSignedIn && !isValidEmail(formState.email)) return false;
  return true;
}

type PledgeAmountFieldProps = {
  amount: number | undefined;
  onAmountChange: (value: number) => void;
  currentPledgeAmount?: number;
  inputId?: string;
  label?: string;
  /** When set, renders an info icon beside the label whose tooltip shows this. */
  tooltip?: ReactNode;
};

export function PledgeAmountField({
  amount,
  onAmountChange,
  currentPledgeAmount,
  inputId,
  label,
  tooltip,
}: PledgeAmountFieldProps) {
  const currentPledgeDisplay =
    currentPledgeAmount === undefined ? undefined : formatUSDShort(currentPledgeAmount);

  return (
    <FieldGroup>
      <Field className="flex flex-col items-center gap-4 border bg-withus-pure-white p-4">
        <div className="flex items-center justify-center gap-1.5">
          <FieldLabel htmlFor={inputId} className="text-center text-balance">
            {label ??
              (currentPledgeDisplay
                ? "Update your pledge amount"
                : "How much would you want to invest?")}
          </FieldLabel>
          {tooltip ? (
            <Tooltip useTouch>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="What does this pledge mean?"
                  className="text-withus-dark-blue/50 transition-colors hover:text-withus-dark-blue focus-visible:rounded-full focus-visible:ring-2 focus-visible:ring-withus-dark-blue/20 focus-visible:outline-none"
                >
                  <InfoIcon className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent color="blue">{tooltip}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <DollarSelector
          inputId={inputId}
          inputClassName="bg-transparent"
          interestAmount={amount ?? 0}
          setInterestAmount={onAmountChange}
        />
      </Field>
    </FieldGroup>
  );
}

type PledgeSupportiveMessageFieldProps = {
  message: string;
  onMessageChange: (value: string) => void;
  disabled?: boolean;
  inputId?: string;
  label?: string;
  placeholder?: string;
};

export function PledgeSupportiveMessageField({
  message,
  onMessageChange,
  disabled = false,
  inputId,
  label = PLEDGE_MESSAGE_LABEL,
  placeholder = PLEDGE_MESSAGE_PLACEHOLDER,
}: PledgeSupportiveMessageFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={inputId} className="text-sm font-medium text-withus-dark-blue">
        {label}
      </Label>
      <textarea
        id={inputId}
        value={message}
        onChange={(e) => onMessageChange(e.target.value)}
        placeholder={placeholder}
        maxLength={SUPPORTIVE_MESSAGE_MAX_LENGTH}
        rows={3}
        className={cn(
          "resize-none border border-withus-dark-blue/20 bg-white px-3 py-2 text-sm text-withus-dark-blue",
          "placeholder:text-withus-dark-blue/40 focus-visible:ring-2 focus-visible:ring-withus-dark-blue/20 focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        disabled={disabled}
      />
      <p className="text-xs text-withus-dark-blue/60">
        {message.length}/{SUPPORTIVE_MESSAGE_MAX_LENGTH} characters
      </p>
    </div>
  );
}

type PledgeNameFieldsProps = {
  firstName: string;
  lastName: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  disabled?: boolean;
  firstNameId?: string;
  lastNameId?: string;
};

export function PledgeNameFields({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  disabled = false,
  firstNameId = "pledge-first-name",
  lastNameId = "pledge-last-name",
}: PledgeNameFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={firstNameId} className="text-sm font-medium text-withus-dark-blue">
          First name *
        </Label>
        <Input
          id={firstNameId}
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          placeholder="First name"
          className="h-10 border-withus-dark-blue/20 bg-white text-withus-dark-blue"
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={lastNameId} className="text-sm font-medium text-withus-dark-blue">
          Last name *
        </Label>
        <Input
          id={lastNameId}
          value={lastName}
          onChange={(e) => onLastNameChange(e.target.value)}
          placeholder="Last name"
          className="h-10 border-withus-dark-blue/20 bg-white text-withus-dark-blue"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

type PledgeConsentFieldProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  inputId?: string;
  label: string;
};

export function PledgeConsentField({
  checked,
  onCheckedChange,
  disabled = false,
  inputId = "pledge-consent",
  label,
}: PledgeConsentFieldProps) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={inputId}
        checked={checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        disabled={disabled}
        className="mt-0.5"
      />
      <Label
        htmlFor={inputId}
        className="cursor-pointer text-sm leading-snug text-withus-dark-blue/80"
      >
        {label}
      </Label>
    </div>
  );
}

type PledgeFormBodyProps = {
  formState: PledgeFormState;
  onFieldChange: <K extends keyof PledgeFormState>(key: K, value: PledgeFormState[K]) => void;
  isPending: boolean;
  /** Drives last-name/email requirements + whether the email field renders. */
  isSignedIn: boolean;
  /** Brand/campaign name woven into the amount-field tooltip copy. */
  businessName: string;
  /** Set once the viewer already has a pledge — switches the amount label copy. */
  currentPledgeAmount?: number;
  /** Namespaces field ids so two instances on a page can't collide. */
  idPrefix?: string;
  /**
   * Amount-field label override. Only the "nominate a new business" step sets
   * this; every other pledge surface uses the default wording.
   */
  amountLabel?: string;
  /**
   * Message-field overrides. Only the "nominate a new business" step sets
   * these; every other pledge surface uses {@link PLEDGE_MESSAGE_LABEL}.
   */
  messageLabel?: string;
  messagePlaceholder?: string;
};

/**
 * The shared pledge field stack — amount, names, anonymous email, supportive
 * message, and consent. Rendered identically by every pledge surface so the
 * inline "Nominate and pledge" form and the standalone {@link PledgeDialog}
 * can't drift apart. Renders as a fragment; the parent owns the surrounding
 * container (spacing, header, footer).
 */
export function PledgeFormBody({
  formState,
  onFieldChange,
  isPending,
  isSignedIn,
  businessName,
  currentPledgeAmount,
  idPrefix = "pledge",
  amountLabel,
  messageLabel = PLEDGE_MESSAGE_LABEL,
  messagePlaceholder = PLEDGE_MESSAGE_PLACEHOLDER,
}: PledgeFormBodyProps) {
  return (
    <>
      <PledgeAmountField
        amount={formState.amount}
        onAmountChange={(value) => onFieldChange("amount", value)}
        currentPledgeAmount={currentPledgeAmount}
        inputId={`${idPrefix}-amount`}
        label={amountLabel}
        tooltip={`This pledge is a non-binding indication of what you might contribute to a future offering from ${businessName}`}
      />

      <PledgeNameFields
        firstName={formState.firstName}
        lastName={formState.lastName}
        onFirstNameChange={(value) => onFieldChange("firstName", value)}
        onLastNameChange={(value) => onFieldChange("lastName", value)}
        disabled={isPending}
        firstNameId={`${idPrefix}-first-name`}
        lastNameId={`${idPrefix}-last-name`}
      />

      {/* Email — only for anonymous pledges; signed-in users pledge under
          their account email. */}
      {isSignedIn ? null : (
        <div className="flex flex-col gap-2">
          <Label
            htmlFor={`${idPrefix}-email`}
            className="text-sm font-medium text-withus-dark-blue"
          >
            Email *
          </Label>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            value={formState.email}
            onChange={(e) => onFieldChange("email", e.target.value)}
            placeholder="you@example.com"
            className="h-10 border-withus-dark-blue/20 bg-white text-withus-dark-blue"
            disabled={isPending}
          />
          <p className="text-xs text-withus-dark-blue/60">
            We'll use this to keep you updated and to link this pledge if you create an account.
          </p>
        </div>
      )}

      <PledgeSupportiveMessageField
        message={formState.supportiveMessage}
        onMessageChange={(value) => onFieldChange("supportiveMessage", value)}
        disabled={isPending}
        inputId={`${idPrefix}-message`}
        label={messageLabel}
        placeholder={messagePlaceholder}
      />

      {/* Privacy is opt-in, so this checkbox is the inverse of the stored
          `consentToDisplay` flag: checking it hides the pledger. */}
      <PledgeConsentField
        checked={!formState.consentToDisplay}
        onCheckedChange={(hide) => onFieldChange("consentToDisplay", !hide)}
        disabled={isPending}
        inputId={`${idPrefix}-consent`}
        label={PLEDGE_CONSENT_LABEL}
      />
    </>
  );
}

type PledgeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formState: PledgeFormState;
  onFormStateChange: (state: PledgeFormState) => void;
  onSubmit: () => void;
  isPending: boolean;
  /** Drives last-name/email requirements + whether the email field renders. */
  isSignedIn: boolean;
  /** Set once the viewer already has a pledge — switches copy to "update". */
  currentPledgeAmount: number | undefined;
  /** Brand/campaign name woven into the dialog + auth-prompt copy. */
  businessName: string;
  /** Submittable gate, computed by the parent (typically via `canSubmitPledge`
   * plus its own change-detection). */
  canSubmit: boolean;

  // ----- post-submit auth prompt (anonymous pledges only) -----
  /** When true, the "create an account" prompt is shown. The parent flips this
   * on in its mutation's `onSuccess` when the pledger is anonymous. */
  authPromptOpen: boolean;
  onAuthPromptOpenChange: (open: boolean) => void;
  /** Set for authenticated investors editing an existing pledge. */
  onDelete?: () => void;
  isDeleting?: boolean;
};

export function PledgeDialog({
  open,
  onOpenChange,
  formState,
  onFormStateChange,
  onSubmit,
  isPending,
  isSignedIn,
  currentPledgeAmount,
  businessName,
  canSubmit,
  authPromptOpen,
  onAuthPromptOpenChange,
  onDelete,
  isDeleting = false,
}: PledgeDialogProps) {
  const updateField = useCallback(
    <K extends keyof PledgeFormState>(key: K, value: PledgeFormState[K]) => {
      onFormStateChange({ ...formState, [key]: value });
    },
    [formState, onFormStateChange],
  );

  const currentPledgeDisplay =
    currentPledgeAmount === undefined ? undefined : formatUSDShort(currentPledgeAmount);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-lg gap-5 p-4 sm:p-8"
          // New pledges render no description; opt out of the a11y link so Radix
          // doesn't warn about a missing `DialogDescription`.
          {...(currentPledgeDisplay ? {} : { "aria-describedby": undefined })}
        >
          <DialogHeader className="gap-3 text-left">
            <DialogTitle className="withus-strong text-2xl text-withus-dark-blue">
              {currentPledgeDisplay ? "Update your pledge" : "Make a pledge"}
            </DialogTitle>
            {currentPledgeDisplay ? (
              <DialogDescription className="text-sm text-withus-dark-blue/70">
                {`You're currently pledged for ${currentPledgeDisplay}. Update your details below if you'd like to change it.`}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <PledgeFormBody
            formState={formState}
            onFieldChange={updateField}
            isPending={isPending || isDeleting}
            isSignedIn={isSignedIn}
            businessName={businessName}
            currentPledgeAmount={currentPledgeAmount}
          />

          <DialogFooter className="sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <Button
              color="darkBlue"
              variant="outline"
              disabled={isPending || isDeleting}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-2 max-sm:mt-2">
              {onDelete && currentPledgeAmount !== undefined ? (
                <Button
                  type="button"
                  color="darkBlue"
                  variant="outline"
                  disabled={isPending || isDeleting}
                  isLoading={isDeleting}
                  onClick={onDelete}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  Delete Pledge
                </Button>
              ) : null}
              <Button
                color="darkBlue"
                disabled={!canSubmit || isDeleting}
                isLoading={isPending}
                onClick={onSubmit}
              >
                {isPending
                  ? "Submitting..."
                  : currentPledgeDisplay
                    ? "Update pledge"
                    : "Confirm pledge"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-submit prompt for anonymous pledgers. The pledge is already
          recorded; this nudges them to create an account so the connect-time
          back-association links the EOI to their new identity. */}
      <AuthnFormDialog
        open={authPromptOpen}
        onOpenChange={onAuthPromptOpenChange}
        title="Stay in the loop"
        description={`Keep up to date with ${businessName}'s progress by creating a WITH.CO account.`}
      />
    </>
  );
}
